import {
  Component,
  EventEmitter,
  Output,
  Input,
  TemplateRef,
  inject,
  OnInit
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {trigger, state, style, transition, animate} from '@angular/animations';
import {FormTypeValue} from '../../../core/constants/form-type.constants';
import {MenuItemComponent} from '../menu-item/menu-item.component';
import {SessionDto} from '../../../core/models/session/sessiondto';
import {RoleConstants} from '../../../core/constants/role.constants';
import {CommonService} from '../../../core/api/services/utility-services/common.service';
import {AuthService} from '../../../core/services/auth.service';
import {Router} from '@angular/router';
import {LocaleService} from '../../../core/services/locale.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuItemsContainer} from '../menu-item/menu-items-container';
import {MenuItem} from '../../../core/models/menu/menu-item';
import {UrlConstants} from '../../../core/constants/url.constants';
import {EventLogReferrerConstants} from '../../../core/constants/event-log-referrer.constants';
import {EventTypeConstants} from '../../../core/constants/event-type.constants';
import {MenuConstants} from '../../../core/constants/menu.constants';

export interface NavigationPanelConfig {
  formType?: FormTypeValue;
  title?: string;
  contentTemplate?: TemplateRef<any>;

  menuItems?: MenuItem[],
}

@Component({
  selector: 'app-navigation-panel',
  standalone: true,
  imports: [
    CommonModule,
    MenuItemComponent
  ],
  templateUrl: './navigation-panel.component.html',
  styleUrl: './navigation-panel.component.css',
})
export class NavigationPanelComponent implements OnInit {
  @Output() closeEvent = new EventEmitter<void>();
  @Input() title: string = '';
  @Input() formType: FormTypeValue = null;
  @Input() contentTemplate: TemplateRef<any> | null = null;


  sessionDetails: SessionDto;
  isCustomer: boolean;
  isPartner: boolean;

  readonly commonService = inject(CommonService);
  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  readonly localeService = inject(LocaleService);
  readonly translateService = inject(TranslateService);

  visibleMenuItems: MenuItem[];

  ngOnInit() {
    this.commonService.sessiondetails?.subscribe(res => {
      this.sessionDetails = res
      this.isCustomer = this.accessible(RoleConstants.CUSTOMER);
      this.isPartner = this.accessible(RoleConstants.PARTNER);
      this.filterMenu();
    });
  }

  menuLoad(menuItems: MenuItem[]): void {
    this.visibleMenuItems = menuItems;

    for (const menuItem of menuItems) {
      if (menuItem.subItem) {
        this.updateMenuItemState(menuItem);
      }
    }
  }

  filterMenu(){
    for(const menuItem of this.visibleMenuItems){
      if (menuItem.label === MenuConstants.CHANGEACCOUNT && !this.sessionDetails?.superUser) {
        const index= this.visibleMenuItems.findIndex(menuItem => menuItem.label=== MenuConstants.CHANGEACCOUNT);
        this.visibleMenuItems.splice(index, 1);
      }
      if (menuItem.label === MenuConstants.CHANGEPASSWORD && !this.showChangePassword()) {
        const index= this.visibleMenuItems.findIndex(menuItem => menuItem.label=== MenuConstants.CHANGEPASSWORD);
        this.visibleMenuItems.splice(index, 1);
      }
    }
  }
  private updateMenuItemState(menuItem: MenuItem): void {
    const children = this.children(menuItem.id);
    if (children.length === 0) {
      return;
    }

    menuItem.open = children.some(child => child.path && this.isOnPage(child.path));
  }

  isOnPage = (page: string): boolean => this.router.url.includes(page);

  accessible(role: string): boolean {
    return this.sessionDetails && this.sessionDetails.userRoles?.includes(role);
  }

  logout() {
    this.commonService.logEvent(EventTypeConstants.LOGOUT, "User is logging out", "", undefined, () => {
      this.authService.logout().subscribe({
        next: (res) => this.authService.redirectAfterLogout(res?.redirectUrl),
        error: (err) => {
          console.error('Logout request failed', err);
          this.authService.redirectAfterLogout(undefined);
        }
      });
    });
  }

  close(): void {
    this.closeEvent.emit();
  }

  toggle(item: MenuItem): void {
    const isLoginPath = item.path === UrlConstants.loginUrl;

    if (item.subItem) {
      this.toggleSubItem(item);
      return;
    }

    if (item.path) {
      this.handlePathNavigation(isLoginPath, item.path);
      this.closeEvent.emit();
    }
  }

  private toggleSubItem(item: MenuItem): void {
    item.open = !item.open;
  }

  private handlePathNavigation(isLoginPath: boolean, path: string): void {
    if (isLoginPath) {
      this.logout();
    } else {
      this.router.navigate([path, {referrer: EventLogReferrerConstants.NAVIGATION}]);
    }
  }

  isVisible(navItem: MenuItem): boolean {

    if (navItem.englishOnly) {
      const locale = this.localeService.getLocale();
      if (locale && locale.localeId != 1) {
        return false;
      }
    }

    if (navItem.parentMenuId == null) {
      return true;
    }

    const parentMenuItem = MenuItemsContainer.PANEL_MENU_ITEMS.find(item => item.id === navItem.parentMenuId);
    return parentMenuItem?.open ?? false;
  }

  private children(parentId: number) {
    return this.visibleMenuItems.filter(i => i.parentMenuId === parentId);
  }

  home() {
    this.router.navigate([UrlConstants.homeUrl]);
    this.closeEvent.emit();
  }

  showChangePassword() {
    const isSharedPartner = this.sessionDetails?.userRoles?.includes(RoleConstants.SHARED_PARTNER);
    return this.sessionDetails?.userName && !isSharedPartner && !this.sessionDetails?.sharedPartnerUser && !this.sessionDetails?.ssouser && !this.sessionDetails.invokedByCrc
  }
}
